import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { palette as T, alpha, type } from '../design';

export function MfaSetupPage({ setPage }) {
  const { user, setupMfa, enableMfa } = useAuth();
  const [step, setStep] = useState('intro');
  const [secret, setSecret] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);


  const handleSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await setupMfa();
      setSecret(data.secret);
      setQrUrl(data.qrCodeUrl);
      setStep('qr');
    } catch (err) {
      setError(err.message || 'Failed to start MFA setup.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await enableMfa(code);
      setBackupCodes(data.backupCodes || []);
      setStep('backup');
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.ivory }}>
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.walnut }}>Please log in to set up MFA.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
      <div style={{ width: '100%', maxWidth: 440, background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px' }}>
        <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: '0 0 8px', textAlign: 'center' }}>
          Two-Factor Authentication
        </h1>
        <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, textAlign: 'center', marginBottom: 24 }}>
          Add an extra layer of security to your account.
        </p>

        {error && (
          <div style={{ background: alpha('errorRed', 'tint'), border: '1px solid ' + alpha('errorRed', 'selection'), borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.errorRed, margin: 0 }}>{error}</p>
          </div>
        )}

        {step === 'intro' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, marginBottom: 20 }}>
              When enabled, you will need to enter a code from your authenticator app each time you log in.
            </p>
            <button
              onClick={handleSetup}
              disabled={loading}
              style={{ background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, cursor: 'pointer' }}
            >
              {loading ? 'Setting up…' : 'Set up MFA'}
            </button>
          </div>
        )}

        {step === 'qr' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, marginBottom: 16 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
            </p>
            {qrUrl && (
              <img src={qrUrl} alt="MFA QR Code" style={{ width: 200, height: 200, marginBottom: 16, borderRadius: 12, border: '1px solid ' + T.border }} />
            )}
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, marginBottom: 16, wordBreak: 'break-all' }}>
              Or enter this key manually: <strong>{secret}</strong>
            </p>
            <form onSubmit={handleEnable} style={{ maxWidth: 280, margin: '0 auto' }}>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
                style={{ width: '100%', padding: '11px 14px', borderRadius: 12, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none', boxSizing: 'border-box', marginBottom: 12, textAlign: 'center' }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{ background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, cursor: 'pointer', width: '100%' }}
              >
                {loading ? 'Verifying…' : 'Enable MFA'}
              </button>
            </form>
          </div>
        )}

        {step === 'backup' && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.sageGreen, margin: '0 0 12px' }}>MFA Enabled</h2>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, marginBottom: 16 }}>
              Save these backup codes in a safe place. They can be used to log in if you lose access to your authenticator app.
            </p>
            <div style={{ background: T.ivory, borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {backupCodes.map((c, i) => (
                <code key={i} style={{ fontFamily: 'monospace', fontSize: type.body, color: T.espresso }}>{c}</code>
              ))}
            </div>
            <button
              onClick={() => setPage('dashboard-student')}
              style={{ background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
